
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";

const COLORS = [
  "#1976d2",
  "#ef5350",
  "#66bb6a",
  "#ffa726",
  "#ab47bc",
  "#26a69a",
];

const initialSummary = {
  totalCollectedRevenue: 0,
  expense: 0,
  profit: 0,
  totalExpectedRevenue: 0,
};

const initialReport = {
  monthly: [],
  quarterly: [],
  yearly: [],
};

const initialGuestBilling = {
  monthly: [],
  quarterly: [],
  yearly: [],
};

const initialPendingFees = {
  totalPending: 0,
  studentsPending: 0,
  pendingPercentage: 0,
};

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ mt: 3 }}>{children}</Box> : null;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const normalizePropertyType = (value) =>
  String(value || "").trim().toUpperCase();

const isHostelOrPG = (type) => {
  const normalized = normalizePropertyType(type);
  return normalized === "HOSTEL" || normalized === "PG";
};

/* ================= COMMON CARD STYLE ================= */

const cardStyle = {
  height: "100%",
  borderRadius: 3,
  border: "1px solid #e8edf3",
  boxShadow: "0 3px 14px rgba(0,0,0,0.035)",
  backgroundColor: "#ffffff",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 7px 22px rgba(0,0,0,0.07)",
    transform: "translateY(-2px)",
  },
};

const chartPaperStyle = {
  p: { xs: 2, sm: 3 },
  height: "100%",
  borderRadius: 3,
  border: "1px solid #e8edf3",
  boxShadow: "0 3px 14px rgba(0,0,0,0.035)",
  minWidth: 0,
};

/* ================= SUMMARY CARD ================= */

const SummaryCard = ({
  title,
  amount,
  subtitle,
  icon,
  color,
  background,
  progress,
}) => (
  <Card elevation={0} sx={cardStyle}>
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
          >
            {title}
          </Typography>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              mt: 1.5,
              color,
              wordBreak: "break-word",
            }}
          >
            ₹ {money(amount)}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.7 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 46,
            height: 46,
            minWidth: 46,
            borderRadius: 2.5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: background,
            color,
          }}
        >
          {icon}
        </Box>
      </Stack>

      {progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, progress))}
          sx={{
            mt: 2.5,
            height: 7,
            borderRadius: 5,
            backgroundColor: "#edf0f5",
            "& .MuiLinearProgress-bar": {
              borderRadius: 5,
              backgroundColor: color,
            },
          }}
        />
      )}
    </CardContent>
  </Card>
);

/* ================= MAIN COMPONENT ================= */

const HostelProfitLoss = () => {
  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState("");

  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );

  const [tab, setTab] = useState(0);

  const [loadingHostels, setLoadingHostels] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [summary, setSummary] = useState(initialSummary);

  const [report, setReport] = useState(initialReport);

  const [guestBilling, setGuestBilling] = useState(
    initialGuestBilling
  );

  const [expenseCategory, setExpenseCategory] = useState([]);
  const [feeCollection, setFeeCollection] = useState([]);

  const [propertyType, setPropertyType] = useState("");

  const [pendingFees, setPendingFees] = useState(
    initialPendingFees
  );

  const [aiInsights, setAiInsights] = useState([]);
  const [profitForecast, setProfitForecast] = useState([]);

  const selectedHostel = hostels.find(
    (h) => String(h.id) === String(hostelId)
  );

  const showHostelFeatures = isHostelOrPG(propertyType);

  /* ================= YEARS ================= */

  const currentYear = new Date().getFullYear();

  const years = [];

  for (let i = currentYear; i >= currentYear - 5; i--) {
    years.push(i.toString());
  }

  /* ================= RESET DASHBOARD ================= */

  const resetDashboard = () => {
    setSummary(initialSummary);
    setReport(initialReport);
    setGuestBilling(initialGuestBilling);
    setExpenseCategory([]);
    setFeeCollection([]);
    setPendingFees(initialPendingFees);
    setAiInsights([]);
    setProfitForecast([]);
    setPropertyType("");
  };

  /* ================= PROPERTY LIST ================= */

  useEffect(() => {
    let active = true;

    const fetchHostels = async () => {
      setLoadingHostels(true);

      try {
        const res = await axiosApi.post(
          "/hostel/getHostelName",
          {}
        );

        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.hostels || [];

        const formatted = list
          .map((item) => {
            if (Array.isArray(item)) {
              return {
                id: item[0],
                hostelName: item[1],
                hostelType: "",
              };
            }

            return {
              id: item.id ?? item.hostelId,
              hostelName: item.hostelName ?? item.name,
              hostelType:
                item.hostelType ??
                item.propertyType ??
                item.accommodationType ??
                "",
            };
          })
          .filter((item) => item.id !== undefined && item.id !== null);

        if (!active) return;

        setHostels(formatted);

        // Automatically select the first property.
        if (formatted.length > 0) {
          setHostelId(formatted[0].id);
        } else {
          setErrorMsg("No properties available.");
        }
      } catch (error) {
        console.error("Property loading failed:", error);

        if (active) {
          setErrorMsg("Unable to load property list.");
        }
      } finally {
        if (active) {
          setLoadingHostels(false);
        }
      }
    };

    fetchHostels();

    return () => {
      active = false;
    };
  }, []);

  /* ================= PROFIT FORECAST ================= */

  const predictNext3Months = (monthlyData) => {
    if (!monthlyData || monthlyData.length < 3) {
      return [];
    }

    const profits = monthlyData.map(
      (m) =>
        Number(
          m.profit ??
          (
            Number(m.totalCollectedRevenue || 0) -
            Number(m.expense || 0)
          )
        )
      );

    const first = profits[0];
    const last = profits[profits.length - 1];

    const growth = (last - first) / profits.length;

    const predictions = [];
    let current = last;

    for (let i = 1; i <= 3; i++) {
      current += growth;

      predictions.push({
        month: `Forecast ${i}`,
        predictedProfit: Math.round(current),
      });
    }

    return predictions;
  };

  /* ================= LOAD FINANCE REPORT ================= */

  useEffect(() => {
    if (!hostelId || loadingHostels) return;

    let active = true;

    const loadReport = async () => {
      setLoadingReport(true);
      setErrorMsg("");

      try {
        const res = await axiosApi.post(
          "hostelExpense/getFinanceReport",
          {
            hostelIds: [hostelId],
            selectedYear,
          }
        );

        if (!active) return;

        const data = res.data || {};

        const responsePropertyType =
          data.propertyType ||
          selectedHostel?.hostelType ||
          "";

        setPropertyType(
          normalizePropertyType(responsePropertyType)
        );

        setSummary({
          totalCollectedRevenue: Number(
            data.totalCollectedRevenue || 0
          ),
          expense: Number(data.totalExpense || 0),
          profit: Number(data.totalProfit || 0),
          totalExpectedRevenue: Number(
            data.totalExpectedRevenue || 0
          ),
        });

        const periodData = data.financePeriodSummaryDTO || {};

        setReport({
          monthly: periodData.monthly || [],
          quarterly: periodData.quarterly || [],
          yearly: periodData.yearly || [],
        });

        setExpenseCategory(data.expenseCategory || []);

        const transformedFeeCollection = (
          data.feeCollection || []
        ).map((m) => ({
          month: m.month,
          expectedFess: m.expectedFess || 0,
          collected: m.collected || 0,
          pendingFess: m.pendingFess || 0,
          ...(m.paymentCategories || {}),
        }));

        setFeeCollection(transformedFeeCollection);

        setPendingFees({
          ...initialPendingFees,
          ...(data.pendingFees || {}),
        });

        setAiInsights(data.aiInsights || []);

        const guestData = data.guestBillingSummary || {};

        setGuestBilling({
          monthly: guestData.monthly || [],
          quarterly: guestData.quarterly || [],
          yearly: guestData.yearly || [],
        });

        const forecast = predictNext3Months(
          periodData.monthly || []
        );

        setProfitForecast(forecast);
      } catch (error) {
        console.error("Finance dashboard load failed:", error);

        if (active) {
          resetDashboard();
          setErrorMsg(
            "Unable to load financial report. Please try again."
          );
        }
      } finally {
        if (active) {
          setLoadingReport(false);
        }
      }
    };

    resetDashboard();
    loadReport();

    return () => {
      active = false;
    };
  }, [
  hostelId,
  selectedYear,
  loadingHostels,
  selectedHostel?.hostelType,
]);

  /* ================= GUEST REVENUE AVAILABILITY ================= */

  const hasGuestRevenue = guestBilling.monthly.some(
    (item) =>
      Number(item.totalExpectedRevenue || 0) > 0 ||
      Number(item.totalCollectedRevenue || 0) > 0
  );

  /* ================= COLLECTION PERCENTAGE ================= */

  const collectionPercentage =
    summary.totalExpectedRevenue > 0
      ? Math.min(
          100,
          Math.max(
            0,
            (summary.totalCollectedRevenue /
              summary.totalExpectedRevenue) *
              100
          )
        )
      : 0;

  const pendingPercentage = Math.min(
    100,
    Math.max(0, Number(pendingFees.pendingPercentage || 0))
  );

  /* ================= CHART HEADER ================= */

  const ChartHeader = ({ title, subtitle }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ fontSize: { xs: 16, sm: 18 } }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  /* ================= FINANCE CHART ================= */

  const FinanceChart = () => (
    <Paper elevation={0} sx={chartPaperStyle}>
      <ChartHeader
        title="Income vs Expense Trend"
        subtitle="Monthly financial performance"
      />

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={report.monthly}
          margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e9edf3"
          />

          <XAxis dataKey="period" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => `₹ ${money(value)}`}
          />
          <Legend />

          <Bar
            dataKey="totalExpectedRevenue"
            name="Expected"
            fill="#1976d2"
            radius={[4, 4, 0, 0]}
          />

          <Bar
            dataKey="totalCollectedRevenue"
            name="Collected"
            fill="#2e7d32"
            radius={[4, 4, 0, 0]}
          />

          <Bar
            dataKey="expense"
            name="Expense"
            fill="#ef5350"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  /* ================= EXPENSE PIE ================= */

  const ExpensePie = () => (
    <Paper elevation={0} sx={chartPaperStyle}>
      <ChartHeader
        title="Expense Breakdown"
        subtitle="Category-wise expense distribution"
      />

      {expenseCategory.length === 0 ? (
        <Box
          sx={{
            height: 270,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">
            No expense category data available
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={expenseCategory}
              dataKey="amount"
              nameKey="category"
              outerRadius={95}
              innerRadius={45}
              paddingAngle={3}
              label
            >
              {expenseCategory.map((entry, index) => (
                <Cell
                  key={index}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>

            <Tooltip
              formatter={(value) => `₹ ${money(value)}`}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );

  /* ================= FEE COLLECTION ================= */

  const FeeCollectionChart = () => (
    <Paper elevation={0} sx={chartPaperStyle}>
      <ChartHeader
        title="Fee Payment Distribution"
        subtitle="Expected, collected and pending fee analysis"
      />

      <ResponsiveContainer width="100%" height={340}>
        <BarChart
          data={feeCollection}
          margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
          />

          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => `₹ ${money(value)}`}
          />
          <Legend />

          <Bar
            dataKey="expectedFess"
            name="Expected"
            fill="#1976d2"
          />

          <Bar
            dataKey="collected"
            name="Collected"
            fill="#2e7d32"
          />

          <Bar
            dataKey="pendingFess"
            name="Pending"
            fill="#ed6c02"
          />

          <Bar
            dataKey="100% Paid"
            name="100% Paid"
            stackId="payment"
            fill="#4caf50"
          />

          <Bar
            dataKey="80-99% Paid"
            name="80-99% Paid"
            stackId="payment"
            fill="#ffa726"
          />

          <Bar
            dataKey="50-79% Paid"
            name="50-79% Paid"
            stackId="payment"
            fill="#42a5f5"
          />

          <Bar
            dataKey="Below 50% Paid"
            name="Below 50% Paid"
            stackId="payment"
            fill="#ab47bc"
          />

          <Bar
            dataKey="Not Paid"
            name="Not Paid"
            stackId="payment"
            fill="#ef5350"
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  /* ================= PENDING FEES ================= */

  const PendingFeesCard = () => (
    <Card elevation={0} sx={cardStyle}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={700}>
            Pending Fees
          </Typography>

          <Box
            sx={{
              width: 43,
              height: 43,
              borderRadius: 2,
              backgroundColor: "#fff0ee",
              color: "#e53935",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TrendingDownIcon />
          </Box>
        </Stack>

        <Typography
          variant="h4"
          fontWeight={800}
          color="error.main"
          sx={{ mt: 2 }}
        >
          ₹ {money(pendingFees.totalPending)}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Students with pending fees: {pendingFees.studentsPending}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Yearly Pending
          </Typography>

          <Typography fontWeight={700}>
            {pendingPercentage}%
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={pendingPercentage}
          color="error"
          sx={{
            height: 8,
            borderRadius: 5,
            backgroundColor: "#fce4ec",
          }}
        />
      </CardContent>
    </Card>
  );

  /* ================= AI INSIGHTS ================= */

  const FinancialInsights = () => (
    <Paper elevation={0} sx={chartPaperStyle}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <TrendingUpIcon color="primary" />

        <Typography variant="h6" fontWeight={700}>
          AI Financial Insights
        </Typography>
      </Stack>

      {aiInsights.length === 0 ? (
        <Typography color="text.secondary">
          No insights available
        </Typography>
      ) : (
        aiInsights.map((insight, index) => (
          <Alert
            key={index}
            severity="info"
            sx={{ mb: 1.5, borderRadius: 2 }}
          >
            {typeof insight === "string"
              ? insight
              : insight.message || insight.description || JSON.stringify(insight)}
          </Alert>
        ))
      )}
    </Paper>
  );

  /* ================= PROFIT FORECAST ================= */

  const ProfitForecastChart = () => (
    <Paper elevation={0} sx={chartPaperStyle}>
      <ChartHeader
        title="Profit Forecast (Next 3 Months)"
        subtitle="Estimated profit based on previous monthly performance"
      />

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={profitForecast}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
          />

          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip
            formatter={(value) => `₹ ${money(value)}`}
          />

          <Bar
            dataKey="predictedProfit"
            name="Predicted Profit"
            fill="#1976d2"
            radius={[5, 5, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  /* ================= GUEST BILLING ================= */

  const GuestBillingChart = () => {
    const chartData = guestBilling.monthly.map((item) => ({
      ...item,
      pendingRevenue:
        Number(item.totalExpectedRevenue || 0) -
        Number(item.totalCollectedRevenue || 0),
    }));

    return (
      <Paper elevation={0} sx={chartPaperStyle}>
        <ChartHeader
          title="Guest Revenue (Short Stay)"
          subtitle="Short-stay guest revenue performance"
        />

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
            />

            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip
              formatter={(value) => `₹ ${money(value)}`}
            />
            <Legend />

            <Bar
              dataKey="totalExpectedRevenue"
              name="Expected"
              fill="#1976d2"
              radius={[4, 4, 0, 0]}
            />

            <Bar
              dataKey="totalCollectedRevenue"
              name="Collected"
              fill="#2e7d32"
              radius={[4, 4, 0, 0]}
            />

            <Bar
              dataKey="pendingRevenue"
              name="Pending"
              fill="#ed6c02"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  /* ================= FINANCE TABLE ================= */

  const FinanceTable = ({ rows = [], showFeeCategories }) => {
    const gridColumns = showFeeCategories
      ? "120px 190px 130px 130px repeat(5, 90px)"
      : "120px 190px 130px 130px";

    const gridStyle = {
      display: "grid",
      gridTemplateColumns: gridColumns,
      alignItems: "center",
      gap: 1,
      py: 1.8,
      px: 1.5,
      minWidth: showFeeCategories ? 1120 : 600,
    };

    const cellRight = {
      textAlign: "right",
      whiteSpace: "nowrap",
    };

    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e8edf3",
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            ...gridStyle,
            fontWeight: 700,
            color: "text.secondary",
            backgroundColor: "#f4f7fb",
            borderBottom: "1px solid #e1e7ef",
          }}
        >
          <Box>Period</Box>
          <Box sx={cellRight}>Collected / Expected</Box>
          <Box sx={cellRight}>Expense</Box>
          <Box sx={cellRight}>Profit</Box>

          {showFeeCategories && (
            <>
              <Box sx={cellRight}>100%</Box>
              <Box sx={cellRight}>80-99%</Box>
              <Box sx={cellRight}>50-79%</Box>
              <Box sx={cellRight}>Below 50%</Box>
              <Box sx={cellRight}>Not Paid</Box>
            </>
          )}
        </Box>

        {rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No financial records available for this period.
            </Typography>
          </Box>
        ) : (
          rows.map((r, i) => {
            const categories = r.paymentCategories || {};

            return (
              <Box
                key={i}
                sx={{
                  ...gridStyle,
                  borderBottom: "1px solid #edf0f4",
                  backgroundColor:
                    i % 2 === 0 ? "#ffffff" : "#fafbfd",
                  "&:last-child": {
                    borderBottom: 0,
                  },
                }}
              >
                <Typography fontWeight={600}>
                  {r.period}
                </Typography>

                <Box sx={cellRight}>
                  <Typography fontWeight={700}>
                    ₹ {money(r.totalCollectedRevenue)}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    of ₹ {money(r.totalExpectedRevenue)}
                  </Typography>
                </Box>

                <Typography sx={cellRight}>
                  ₹ {money(r.expense)}
                </Typography>

                <Typography
                  sx={{
                    ...cellRight,
                    color:
                      Number(r.profit || 0) >= 0
                        ? "success.main"
                        : "error.main",
                    fontWeight: 700,
                  }}
                >
                  ₹ {money(r.profit)}
                </Typography>

                {showFeeCategories && (
                  <>
                    <Typography sx={cellRight}>
                      {categories["100% Paid"] || 0}
                    </Typography>

                    <Typography sx={cellRight}>
                      {categories["80-99% Paid"] || 0}
                    </Typography>

                    <Typography sx={cellRight}>
                      {categories["50-79% Paid"] || 0}
                    </Typography>

                    <Typography sx={cellRight}>
                      {categories["Below 50% Paid"] || 0}
                    </Typography>

                    <Typography sx={cellRight}>
                      {categories["Not Paid"] || 0}
                    </Typography>
                  </>
                )}
              </Box>
            );
          })
        )}
      </Paper>
    );
  };

  /* ================= MAIN UI ================= */

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: "100vh",
          background: "#f4f6fa",
          py: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            px: { xs: 1.5, sm: 3 },
          }}
        >
          {/* HEADER */}

<Paper
  elevation={0}
  sx={{
    position: "relative",
    overflow: "hidden",
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 2.5 },
    mb: 3,
    minHeight: { xs: 82, sm: 100 },
    borderRadius: 2.5,
    color: "#fff",
    background:
      "linear-gradient(120deg, #123f73 0%, #1768ad 55%, #2588db 100%)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 5px 18px rgba(20,89,166,0.16)",
  }}
>
  {/* Decorative background */}
  <Box
    sx={{
      position: "absolute",
      width: 180,
      height: 180,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.055)",
      right: -45,
      top: -95,
      pointerEvents: "none",
    }}
  />

  <Stack
    direction="row"
    alignItems="center"
    spacing={{ xs: 1.5, sm: 2 }}
    sx={{ position: "relative", zIndex: 1 }}
  >
    <Box
      sx={{
        width: { xs: 44, sm: 52 },
        height: { xs: 44, sm: 52 },
        minWidth: { xs: 44, sm: 52 },
        borderRadius: 2,
        background: "rgba(255,255,255,0.16)",
        border: "1px solid rgba(255,255,255,0.22)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AccountBalanceWalletIcon
        sx={{ fontSize: { xs: 25, sm: 30 } }}
      />
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: { xs: 18, sm: 23 },
          fontWeight: 750,
          lineHeight: 1.3,
          letterSpacing: "0.15px",
          color: "#fff",
        }}
      >
        Property Financial Dashboard
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: { xs: 12, sm: 13.5 },
          fontWeight: 400,
          lineHeight: 1.5,
          color: "rgba(255,255,255,0.85)",
        }}
      >
        Monitor revenue, expenses, profit and financial performance.
      </Typography>
    </Box>
  </Stack>
</Paper>


          {/* ERROR */}

          {errorMsg && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setErrorMsg("")}
            >
              {errorMsg}
            </Alert>
          )}

          {/* PROPERTY FILTER */}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              border: "1px solid #e8edf3",
              boxShadow: "0 3px 14px rgba(0,0,0,0.025)",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <HomeWorkIcon color="primary" />

              <Typography variant="h6" fontWeight={700}>
                Dashboard Filters
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Property</InputLabel>

                  <Select
                    value={hostelId}
                    label="Property"
                    disabled={loadingHostels || hostels.length === 0}
                    onChange={(e) => {
                      setHostelId(e.target.value);
                    }}
                  >
                    {hostels.map((h) => (
                      <MenuItem key={h.id} value={h.id}>
                        {h.hostelName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>

                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) =>
                      setSelectedYear(e.target.value)
                    }
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                {selectedHostel?.hostelName
                  ? `Showing financial report for ${selectedHostel.hostelName}`
                  : "Select a property to view its financial report."}
              </Typography>

              <Chip
                size="small"
                color={loadingReport ? "warning" : "success"}
                variant="outlined"
                label={
                  loadingReport
                    ? "Updating dashboard..."
                    : "Auto updated"
                }
              />
            </Stack>
          </Paper>

          {/* LOADING */}

          {loadingHostels || loadingReport ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 3,
                textAlign: "center",
                border: "1px solid #e8edf3",
              }}
            >
              <CircularProgress />

              <Typography
                color="text.secondary"
                sx={{ mt: 2 }}
              >
                {loadingHostels
                  ? "Loading properties..."
                  : "Loading financial dashboard..."}
              </Typography>

              <LinearProgress sx={{ mt: 3, borderRadius: 5 }} />
            </Paper>
          ) : hostelId && !errorMsg ? (
            <>
              {/* SUMMARY CARDS */}

              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <SummaryCard
                    title="Total Collected Revenue"
                    amount={summary.totalCollectedRevenue}
                    subtitle={`Expected: ₹ ${money(
                      summary.totalExpectedRevenue
                    )}`}
                    icon={<AccountBalanceWalletIcon />}
                    color="#1976d2"
                    background="#eaf3ff"
                    progress={collectionPercentage}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <SummaryCard
                    title="Total Expense"
                    amount={summary.expense}
                    subtitle="Total operating expenses"
                    icon={<TrendingDownIcon />}
                    color="#e53935"
                    background="#fff0ee"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <SummaryCard
                    title="Net Profit"
                    amount={summary.profit}
                    subtitle="Revenue minus expenses"
                    icon={<TrendingUpIcon />}
                    color={
                      summary.profit >= 0
                        ? "#2e7d32"
                        : "#e53935"
                    }
                    background={
                      summary.profit >= 0
                        ? "#e8f5e9"
                        : "#fff0ee"
                    }
                  />
                </Grid>
              </Grid>

              {/* FINANCE CHARTS */}

              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={7}>
                  <FinanceChart />
                </Grid>

                <Grid item xs={12} lg={5}>
                  <ExpensePie />
                </Grid>
              </Grid>

              {/* HOSTEL / PG ONLY */}

              {showHostelFeatures && (
                <Grid
                  container
                  spacing={2.5}
                  sx={{ mb: 3 }}
                >
                  <Grid item xs={12} lg={8}>
                    <FeeCollectionChart />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <PendingFeesCard />
                  </Grid>
                </Grid>
              )}

              {/* AI INSIGHTS */}

              <Box sx={{ mb: 3 }}>
                <FinancialInsights />
              </Box>

              {/* PROFIT FORECAST */}

              {profitForecast.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <ProfitForecastChart />
                </Box>
              )}

              {/* GUEST REVENUE ONLY WHEN AVAILABLE */}

              {hasGuestRevenue && (
                <Box sx={{ mb: 3 }}>
                  <GuestBillingChart />
                </Box>
              )}

              {/* MONTHLY / QUARTERLY / YEARLY TABLE */}

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 3,
                  border: "1px solid #e8edf3",
                  boxShadow: "0 3px 14px rgba(0,0,0,0.025)",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mb: 2 }}
                >
                  Financial Summary
                </Typography>

                <Tabs
                  value={tab}
                  onChange={(e, value) => setTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    borderBottom: "1px solid #edf0f4",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      minWidth: 100,
                    },
                  }}
                >
                  <Tab label="Monthly" />
                  <Tab label="Quarterly" />
                  <Tab label="Yearly" />
                </Tabs>

                <TabPanel value={tab} index={0}>
                  <FinanceTable
                    rows={report.monthly}
                    showFeeCategories={showHostelFeatures}
                  />
                </TabPanel>

                <TabPanel value={tab} index={1}>
                  <FinanceTable
                    rows={report.quarterly}
                    showFeeCategories={showHostelFeatures}
                  />
                </TabPanel>

                <TabPanel value={tab} index={2}>
                  <FinanceTable
                    rows={report.yearly}
                    showFeeCategories={showHostelFeatures}
                  />
                </TabPanel>
              </Paper>
            </>
          ) : (
            !loadingHostels && (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: "center",
                  borderRadius: 3,
                }}
              >
                <HomeWorkIcon
                  sx={{
                    fontSize: 45,
                    color: "text.secondary",
                    mb: 1,
                  }}
                />

                <Typography fontWeight={700}>
                  No Property Selected
                </Typography>

                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mt: 1 }}
                >
                  Please select a property to view the financial dashboard.
                </Typography>
              </Paper>
            )
          )}
        </Box>
      </Box>
    </>
  );
};

export default HostelProfitLoss;
